// safety/safety-walks/pages/ObservationDetailPage.tsx
// Observation detail (Miro: "Responsible Person or Safety Officer Obs Detail
// Screen"). Full screen: description, before photo, Action Details panel,
// notification status list, corrective action (closed state), activity
// timeline (audit trail), and role-aware actions:
//   - RP (assigned): comment, Close Observation (-> close-out form)
//   - SO: Edit (-> edit form: details + extra photos), comment,
//     verify/return (Pending Verification), force-close override
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Loader2, Send, ImageOff, CheckCircle2, RotateCcw, ShieldAlert, Camera, MessageSquarePlus, AlertTriangle, Trash2, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { useAbility } from '@casl/react';
import { AbilityContext } from '../../../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../../../constants/permissions.constants';
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogCancel,
} from '../../../../components/ui/alert-dialog';
import {
    getObservation,
    addComment,
    verifyObservation,
    returnObservation,
    forceCloseObservation,
    deleteObservation,
} from '../../../../utils/safety-walks-api';
import { StatusPill, RiskBadge, DueIndicator, fmtDate, PhotoThumb, PhotoLightbox } from '../components/shared';
import type { SafetyObservation, AuditEntry, ActivityComment, PhotoMeta } from '../../../../types/safety-walk.types';

const NOTIFICATION_STEPS = ['Assigned', 'Due in 7 days', 'Due in 3 days', 'Due tomorrow', 'Overdue alert'];

export const ObservationDetailPage: React.FC = () => {
    const { obsId } = useParams<{ obsId: string }>();
    const id = Number(obsId);
    const navigate = useNavigate();
    const ability = useAbility(AbilityContext);

    const [obs, setObs] = useState<SafetyObservation | null>(null);
    const [loading, setLoading] = useState(true);
    const [commentText, setCommentText] = useState('');
    const [posting, setPosting] = useState(false);
    const [acting, setActing] = useState(false);

    // Verify / Return / Force-Close confirmation dialogs (AlertDialog,
    // matching the Hazardous Chemical archive modal UX — no browser alerts).
    const [verifyOpen, setVerifyOpen] = useState(false);
    const [verifyComments, setVerifyComments] = useState('');
    const [returnOpen, setReturnOpen] = useState(false);
    const [returnReason, setReturnReason] = useState('');
    const [forceCloseOpen, setForceCloseOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    // True when the observation was soft-deleted — email/in-app notification
    // links still point at this page, which renders the deleted empty state.
    const [deleted, setDeleted] = useState(false);

    // Lightbox preview for before/after photos (full-resolution view)
    const [previewPhoto, setPreviewPhoto] = useState<PhotoMeta | null>(null);

    const canVerify = ability.can(ACTIONS.VERIFY, RESOURCES.SAFETY_OBSERVATION);
    const canReturn = ability.can(ACTIONS.RETURN, RESOURCES.SAFETY_OBSERVATION);
    const canForceClose = ability.can(ACTIONS.UPDATE, RESOURCES.SAFETY_OBSERVATION);
    const canEdit = ability.can(ACTIONS.UPDATE, RESOURCES.SAFETY_OBSERVATION);
    const canCloseOut = ability.can(ACTIONS.CLOSE_OUT, RESOURCES.SAFETY_OBSERVATION);
    const canDelete = ability.can(ACTIONS.DELETE, RESOURCES.SAFETY_OBSERVATION);

    const fetchObs = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        try {
            const res = await getObservation(id);
            setObs(res.observation || null);
            setDeleted(false);
        } catch (error: any) {
            // A soft-deleted observation 404s with a distinct code — email and
            // in-app notification links still point here, so render the
            // deleted empty state.
            if (error?.status === 404 && error?.data?.code === 'OBSERVATION_DELETED') {
                setObs(null);
                setDeleted(true);
            }
            // api util toasts
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchObs();
    }, [fetchObs]);

    const postComment = async () => {
        if (!commentText.trim()) return;
        setPosting(true);
        try {
            const res = await addComment(id, commentText.trim());
            setObs(res.observation);
            setCommentText('');
        } catch (error: any) {
            toast.error(error.message || 'Failed to post comment');
        } finally {
            setPosting(false);
        }
    };

    const confirmVerify = async () => {
        if (!obs) return;
        setActing(true);
        try {
            const res = await verifyObservation(id, verifyComments.trim() || undefined);
            setObs(res.observation);
            toast.success('Observation closed');
            setVerifyOpen(false);
            setVerifyComments('');
        } catch (error: any) {
            toast.error(error.message || 'Verification failed');
        } finally {
            setActing(false);
        }
    };

    const confirmReturn = async () => {
        if (!obs) return;
        setActing(true);
        try {
            const res = await returnObservation(id, returnReason.trim() || undefined);
            setObs(res.observation);
            toast.success('Close-out returned');
            setReturnOpen(false);
            setReturnReason('');
        } catch (error: any) {
            toast.error(error.message || 'Failed to return close-out');
        } finally {
            setActing(false);
        }
    };

    const confirmForceClose = async () => {
        if (!obs) return;
        setActing(true);
        try {
            const res = await forceCloseObservation(id);
            setObs(res.observation);
            toast.success('Observation force-closed');
            setForceCloseOpen(false);
        } catch (error: any) {
            toast.error(error.message || 'Force-close failed');
        } finally {
            setActing(false);
        }
    };

    const confirmDelete = async () => {
        if (!obs) return;
        setActing(true);
        try {
            await deleteObservation(id);
            toast.success('Observation deleted');
            setDeleteOpen(false);
            navigate(obs.walk ? `/safety/walks/${obs.walk.id}` : '/safety/observations');
        } catch (error: any) {
            toast.error(error.message || 'Failed to delete observation');
        } finally {
            setActing(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-24">
                <Loader2 size={24} className="animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (!obs) {
        return (
            <div className="max-w-[1200px] mx-auto px-4 py-16 text-center text-muted-foreground">
                {deleted ? (
                    <>
                        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-muted">
                            <Trash2 size={24} className="text-muted-foreground" />
                        </div>
                        <p className="text-base font-medium text-foreground">
                            Observation has been deleted. Speak to the Safety Officer if you think this is a mistake.
                        </p>
                        <button
                            onClick={() => navigate('/safety/observations')}
                            className="mt-6 inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"
                        >
                            Back to Safety Observations
                        </button>
                    </>
                ) : (
                    'Observation not found.'
                )}
            </div>
        );
    }

    const backTarget = obs.walk ? `/safety/walks/${obs.walk.id}` : '/safety/observations';
    const audit: AuditEntry[] = Array.isArray(obs.audit) ? obs.audit : [];
    const comments: ActivityComment[] = Array.isArray(obs.comments) ? obs.comments : [];

    // The activity feed = audit trail + comment messages interleaved by date.
    // A comment's own entry carries the text, so the redundant 'Comment Added'
    // audit marker is folded into it instead of shown twice.
    const trail: { id: string; action: string; who: string; date: string; details?: Record<string, unknown> | null; text?: string | null }[] = [
        ...audit
            .filter((a) => a.action !== 'Comment Added')
            .map((a) => ({ id: a.id, action: a.action, who: a.who, date: a.date, details: a.details, text: null })),
        ...comments.map((c) => ({ id: c.id, action: 'Comment', who: c.authorName || '?', date: c.date, text: c.text })),
    ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    return (
        <div className="pb-10 px-0 sm:px-4 lg:px-0 max-w-[1400px] mx-auto w-full">
            {/* Header */}
            <div className="flex items-start justify-between mb-5 gap-3 flex-wrap">
                <div>
                    <button onClick={() => navigate(backTarget)} className="text-sm text-primary hover:underline mb-1 inline-flex items-center gap-1">
                        <ChevronLeft size={14} /> {obs.walk ? `${obs.walk.walkNumber} — Observations` : 'My Safety Observations'}
                    </button>
                    <div className="flex items-center gap-2 flex-wrap">
                        <h1 className="text-2xl font-medium">{obs.obsNumber}</h1>
                        <RiskBadge risk={obs.riskRating} />
                        {obs.category && <span className="text-sm text-muted-foreground">{obs.category}</span>}
                        <StatusPill status={obs.status} />
                        <DueIndicator isOverdue={obs.isOverdue} dueDate={obs.dueDate} />
                    </div>
                    <p className="text-sm font-medium mt-1">{obs.title || 'Untitled observation'}</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {canEdit && obs.status !== 'Closed' && (
                        <button
                            onClick={() => navigate(`/safety/observations/${id}/edit`)}
                            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-border bg-card text-sm font-medium text-foreground hover:bg-accent"
                        >
                            <Pencil size={16} /> Edit
                        </button>
                    )}
                    {canCloseOut && obs.status !== 'Closed' && obs.status !== 'Pending Verification' && (
                        <button
                            onClick={() => navigate(`/safety/observations/${id}/close-out`)}
                            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"
                        >
                            <CheckCircle2 size={16} /> Close Observation
                        </button>
                    )}
                    {canVerify && obs.status === 'Pending Verification' && (
                        <button
                            onClick={() => { setVerifyComments(''); setVerifyOpen(true); }}
                            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700"
                        >
                            <CheckCircle2 size={16} /> Verify
                        </button>
                    )}
                    {canReturn && obs.status === 'Pending Verification' && (
                        <button
                            onClick={() => { setReturnReason(''); setReturnOpen(true); }}
                            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-amber-300 bg-amber-50 text-amber-700 text-sm font-medium hover:bg-amber-100"
                        >
                            <RotateCcw size={16} /> Return
                        </button>
                    )}
                    {canForceClose && (obs.status === 'Open' || obs.status === 'In Progress') && (
                        <button
                            onClick={() => setForceCloseOpen(true)}
                            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm font-medium hover:bg-red-100"
                        >
                            <ShieldAlert size={16} /> Force Close
                        </button>
                    )}
                    {canDelete && (
                        <button
                            onClick={() => setDeleteOpen(true)}
                            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700"
                        >
                            <Trash2 size={16} /> Delete
                        </button>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left: description + photos + timeline */}
                <div className="lg:col-span-2 space-y-6">
                    <div className="rounded-xl border border-border bg-card p-5">
                        <h3 className="text-sm font-semibold mb-3">Observation Description</h3>
                        <p className="text-sm text-foreground whitespace-pre-wrap">{obs.description || 'No description provided.'}</p>
                        <div className="mt-4 flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                            <span>Recorded: {fmtDate(obs.createdAt)}</span>
                            {obs.walk && <span>Walk: {obs.walk.walkNumber} ({obs.walk.project})</span>}
                        </div>
                    </div>

                    {/* Before photos (collection) */}
                    <div className="rounded-xl border border-border bg-card p-5">
                        <h3 className="text-sm font-semibold mb-3">Before Photos</h3>
                        {Array.isArray(obs.beforePhoto) && obs.beforePhoto.length > 0 ? (
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                {obs.beforePhoto.map((photo) => (
                                    <PhotoThumb key={photo.id} photo={photo} onView={setPreviewPhoto} />
                                ))}
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border py-10 text-muted-foreground">
                                <ImageOff size={28} className="mb-2" />
                                <span className="text-xs">No photo uploaded</span>
                            </div>
                        )}
                    </div>

                    {/* Corrective action (shown once documented) */}
                    {(obs.correctiveAction || obs.status === 'Closed') && (
                        <div className="rounded-xl border border-border bg-card p-5">
                            <h3 className="text-sm font-semibold mb-3">Corrective Action</h3>
                            <p className="text-sm whitespace-pre-wrap">{obs.correctiveAction || '—'}</p>
                            {obs.resolutionNotes && (
                                <div className="mt-4">
                                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Resolution Notes</p>
                                    <p className="text-sm whitespace-pre-wrap">{obs.resolutionNotes}</p>
                                </div>
                            )}
                            <div className="mt-4 flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
                                {obs.fixedDate && <span>Fixed: {fmtDate(obs.fixedDate)}</span>}
                                {obs.verifiedByName && <span>Verified by: {obs.verifiedByName}</span>}
                            </div>
                            {Array.isArray(obs.afterPhoto) && obs.afterPhoto.length > 0 && (
                                <div className="mt-4">
                                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">After Photos</p>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                        {obs.afterPhoto.map((photo) => (
                                            <PhotoThumb key={photo.id} photo={photo} onView={setPreviewPhoto} />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Activity timeline — audit trail + comment messages */}
                    <div className="rounded-xl border border-border bg-card p-5">
                        <h3 className="text-sm font-semibold mb-4">Activity Timeline</h3>
                        <div className="space-y-1">
                            {trail.map((entry, i) => (
                                <div key={entry.id} className="flex gap-4">
                                    <div className="flex flex-col items-center">
                                        <span className="h-2.5 w-2.5 rounded-full bg-primary mt-1.5" />
                                        {i < trail.length - 1 && <div className="w-px flex-1 bg-border my-1" />}
                                    </div>
                                    <div className="pb-6">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-sm font-medium">{entry.action}</span>
                                            <span className="text-xs text-muted-foreground">
                                                {entry.who} • {new Date(entry.date).toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                            </span>
                                        </div>
                                        {entry.text ? (
                                            <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{entry.text}</p>
                                        ) : entry.details ? (
                                            <pre className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap font-sans">{JSON.stringify(entry.details)}</pre>
                                        ) : null}
                                    </div>
                                </div>
                            ))}
                            {trail.length === 0 && (
                                <p className="text-sm text-muted-foreground">No activity yet</p>
                            )}
                        </div>

                        {obs.status !== 'Closed' && (
                            <div className="flex items-center gap-2 mt-5">
                                <input
                                    value={commentText}
                                    onChange={(e) => setCommentText(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && postComment()}
                                    placeholder="Add a comment..."
                                    className="flex-1 h-10 px-3 rounded-lg border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                                />
                                <button
                                    onClick={postComment}
                                    disabled={posting || !commentText.trim()}
                                    className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
                                >
                                    {posting ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                                    Post
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Right: action details + notification status */}
                <div className="space-y-6">
                    <div className="rounded-xl border border-border bg-card p-5">
                        <h3 className="text-sm font-semibold mb-4">Action Details</h3>
                        <div className="space-y-3 text-sm">
                            <div>
                                <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-0.5">Person Responsible</div>
                                <div className="font-medium">{obs.personResponsibleName || '—'}</div>
                            </div>
                            <div>
                                <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-0.5">Company</div>
                                <div className="font-medium">{obs.company || '—'}</div>
                            </div>
                            <div>
                                <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-0.5">Due Date</div>
                                <div className="font-medium">{fmtDate(obs.dueDate)}</div>
                            </div>
                            {obs.walk && (
                                <div>
                                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-0.5">Walk</div>
                                    <div className="font-medium">{obs.walk.walkNumber} — {obs.walk.project || '—'}</div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="rounded-xl border border-border bg-card p-5">
                        <h3 className="text-sm font-semibold mb-4">Notifications</h3>
                        <div className="space-y-2">
                            {NOTIFICATION_STEPS.map((step, i) => {
                                const isOverdueStep = step === 'Overdue alert';
                                const active = isOverdueStep ? !!obs.isOverdue : i <= notificationIndex(obs);
                                return (
                                    <div key={step} className={`flex items-center justify-between text-sm ${active ? '' : 'opacity-40'}`}>
                                        <span>{step}</span>
                                        {active ? (
                                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                                                isOverdueStep ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-600'
                                            }`}>
                                                {isOverdueStep ? 'Fired' : 'Sent'}
                                            </span>
                                        ) : (
                                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">Pending</span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>

            {/* Verify confirmation dialog */}
            <AlertDialog open={verifyOpen} onOpenChange={setVerifyOpen}>
                <AlertDialogContent className="sm:max-w-[440px] p-0 gap-0 overflow-hidden rounded-2xl border border-gray-200">
                    <AlertDialogHeader className="p-6 pb-4">
                        <div className="flex items-center gap-2">
                            <CheckCircle2 size={20} className="text-emerald-600" />
                            <AlertDialogTitle className="text-lg font-bold text-gray-900">Verify & Close Observation</AlertDialogTitle>
                        </div>
                        <AlertDialogDescription className="text-sm text-gray-500 font-medium pt-1">
                            Are you sure you want to verify and close <span className="font-bold text-gray-700">{obs.obsNumber}</span>?
                            This confirms the corrective action is complete and moves the observation to <span className="font-bold text-gray-700">Closed</span>.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="px-6 pb-4">
                        <label className="text-xs font-medium text-gray-500 mb-1.5 block">VERIFICATION COMMENTS (OPTIONAL)</label>
                        <textarea
                            value={verifyComments}
                            onChange={(e) => setVerifyComments(e.target.value)}
                            placeholder="Add comments from the verifying officer..."
                            rows={2}
                            className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                        />
                    </div>
                    <AlertDialogFooter className="px-6 pb-6 gap-2">
                        <AlertDialogCancel
                            className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-10 rounded-xl text-sm font-bold px-5"
                            disabled={acting}
                        >
                            Cancel
                        </AlertDialogCancel>
                        <button
                            className="bg-emerald-600 hover:bg-emerald-700 text-white h-10 rounded-xl text-sm font-bold px-5 inline-flex items-center gap-2"
                            onClick={confirmVerify}
                            disabled={acting}
                        >
                            {acting ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                            Verify & Close
                        </button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Return confirmation dialog */}
            <AlertDialog open={returnOpen} onOpenChange={setReturnOpen}>
                <AlertDialogContent className="sm:max-w-[440px] p-0 gap-0 overflow-hidden rounded-2xl border border-gray-200">
                    <AlertDialogHeader className="p-6 pb-4">
                        <div className="flex items-center gap-2">
                            <AlertTriangle size={20} className="text-amber-500" />
                            <AlertDialogTitle className="text-lg font-bold text-gray-900">Return Close-Out</AlertDialogTitle>
                        </div>
                        <AlertDialogDescription className="text-sm text-gray-500 font-medium pt-1">
                            Are you sure you want to return <span className="font-bold text-gray-700">{obs.obsNumber}</span>?
                            The close-out will move back to <span className="font-bold text-gray-700">In Progress</span> and the responsible person will be notified.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="px-6 pb-4">
                        <label className="text-xs font-medium text-gray-500 mb-1.5 block">REASON FOR RETURN *</label>
                        <textarea
                            value={returnReason}
                            onChange={(e) => setReturnReason(e.target.value)}
                            placeholder="Explain why the close-out is being returned..."
                            rows={2}
                            className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                        />
                    </div>
                    <AlertDialogFooter className="px-6 pb-6 gap-2">
                        <AlertDialogCancel
                            className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-10 rounded-xl text-sm font-bold px-5"
                            disabled={acting}
                        >
                            Cancel
                        </AlertDialogCancel>
                        <button
                            className="bg-amber-500 hover:bg-amber-600 text-white h-10 rounded-xl text-sm font-bold px-5 inline-flex items-center gap-2"
                            onClick={confirmReturn}
                            disabled={acting || !returnReason.trim()}
                        >
                            {acting ? <Loader2 size={16} className="animate-spin" /> : <RotateCcw size={16} />}
                            Return Close-Out
                        </button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Force Close confirmation dialog */}
            <AlertDialog open={forceCloseOpen} onOpenChange={setForceCloseOpen}>
                <AlertDialogContent className="sm:max-w-[440px] p-0 gap-0 overflow-hidden rounded-2xl border border-gray-200">
                    <AlertDialogHeader className="p-6 pb-4">
                        <div className="flex items-center gap-2">
                            <ShieldAlert size={20} className="text-red-500" />
                            <AlertDialogTitle className="text-lg font-bold text-gray-900">Force Close Observation</AlertDialogTitle>
                        </div>
                        <AlertDialogDescription className="text-sm text-gray-500 font-medium pt-1">
                            Are you sure you want to force-close <span className="font-bold text-gray-700">{obs.obsNumber}</span>?
                            This bypasses the verification step and moves the observation directly to
                            <span className="font-bold text-gray-700"> Closed</span>. Only use this for an admin override.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="px-6 pb-6 gap-2">
                        <AlertDialogCancel
                            className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-10 rounded-xl text-sm font-bold px-5"
                            disabled={acting}
                        >
                            Cancel
                        </AlertDialogCancel>
                        <button
                            className="bg-red-600 hover:bg-red-700 text-white h-10 rounded-xl text-sm font-bold px-5 inline-flex items-center gap-2"
                            onClick={confirmForceClose}
                            disabled={acting}
                        >
                            {acting ? <Loader2 size={16} className="animate-spin" /> : <ShieldAlert size={16} />}
                            Force Close
                        </button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Delete confirmation dialog */}
            <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                <AlertDialogContent className="sm:max-w-[440px] p-0 gap-0 overflow-hidden rounded-2xl border border-gray-200">
                    <AlertDialogHeader className="p-6 pb-4">
                        <div className="flex items-center gap-2">
                            <Trash2 size={20} className="text-red-500" />
                            <AlertDialogTitle className="text-lg font-bold text-gray-900">Delete Observation</AlertDialogTitle>
                        </div>
                        <AlertDialogDescription className="text-sm text-gray-500 font-medium pt-1">
                            Are you sure you want to delete <span className="font-bold text-gray-700">{obs.obsNumber}</span>?
                            This removes it from the register — the responsible person will no longer be able to act on it.
                            Only delete an observation that was created incorrectly.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="px-6 pb-6 gap-2">
                        <AlertDialogCancel
                            className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-10 rounded-xl text-sm font-bold px-5"
                            disabled={acting}
                        >
                            Cancel
                        </AlertDialogCancel>
                        <button
                            className="bg-red-600 hover:bg-red-700 text-white h-10 rounded-xl text-sm font-bold px-5 inline-flex items-center gap-2"
                            onClick={confirmDelete}
                            disabled={acting}
                        >
                            {acting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                            Delete Observation
                        </button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Photo lightbox — full-resolution view */}
            <PhotoLightbox photo={previewPhoto} onClose={() => setPreviewPhoto(null)} />
        </div>
    );
};

// Which reminder tiers have fired based on due date distance from today
function notificationIndex(obs: SafetyObservation): number {
    if (!obs.dueDate) return 0;
    const today = new Date().toISOString().slice(0, 10);
    const days = Math.ceil((new Date(`${obs.dueDate}T00:00:00`).getTime() - new Date(`${today}T00:00:00`).getTime()) / 86400000);
    if (days <= 1) return 3; // due tomorrow
    if (days <= 3) return 2; // due in 3 days
    if (days <= 7) return 1; // due in 7 days
    return 0; // assigned only
}
